import React, { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/media";
import { MaskReveal, Reveal } from "@/components/motion/Reveal";

const SHOTS = [
  { src: IMAGES.galleryPortrait, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "Studio Portrait", place: "Editorial" },
  { src: IMAGES.experienceFestival, w: "w-[88vw] sm:w-[620px]", ratio: "aspect-[16/10]", label: "Escape", place: "Amsterdam" },
  { src: IMAGES.branded, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "DaniQ Live", place: "Main Show" },
  { src: IMAGES.decks, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "In The Mix", place: "Live" },
  { src: IMAGES.experienceLounge, w: "w-[88vw] sm:w-[620px]", ratio: "aspect-[16/10]", label: "Sunset Set", place: "Ibiza" },
  { src: IMAGES.mcGold, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "On The Mic", place: "Stage" },
  { src: IMAGES.drumSmile, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "The Kit", place: "Backstage" },
  { src: IMAGES.escapeNeon, w: "w-[78vw] sm:w-[360px]", ratio: "aspect-[3/4]", label: "Neon Nights", place: "Club" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);
  const stripRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false });

  const onScroll = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  }, []);

  const onPointerDown = (e) => {
    const el = stripRef.current;
    drag.current = { down: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    const el = stripRef.current;
    if (!drag.current.down || !el) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 6) drag.current.moved = true;
    el.scrollLeft = drag.current.scrollLeft - dx;
  };
  const endDrag = () => { drag.current.down = false; };

  const openIfNotDragging = (s) => { if (!drag.current.moved) setActive(s); };

  return (
    <section id="gallery" className="relative py-28 lg:py-40 overflow-hidden">
      <div className="px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><span className="text-xs uppercase tracking-[0.4em] text-gold">Gallery</span></Reveal>
            <h2 className="mt-4 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-wide text-white">
              <MaskReveal>THE ENERGY,</MaskReveal>
              <MaskReveal delay={0.1}>IN <span className="text-magenta">FRAMES</span></MaskReveal>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <div className="flex items-center gap-3 text-white/40">
              <span className="text-[11px] uppercase tracking-[0.3em]">Drag to explore</span>
              <ArrowRight className="h-4 w-4 text-magenta" />
            </div>
          </Reveal>
        </div>
      </div>

      {/* Filmstrip */}
      <div
        ref={stripRef}
        data-testid="gallery-strip"
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="flex gap-5 sm:gap-8 overflow-x-auto px-6 lg:px-16 pb-2 cursor-grab active:cursor-grabbing [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {SHOTS.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-5%" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1], delay: (i % 4) * 0.05 }}
            className={`relative shrink-0 ${s.w} ${i % 2 === 1 ? "mt-10" : ""}`}
          >
            <span className="mb-3 flex items-baseline gap-2">
              <span className="font-display text-2xl text-magenta tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">{s.place}</span>
            </span>
            <button
              data-testid={`gallery-item-${i}`}
              onClick={() => openIfNotDragging(s)}
              className={`group relative block w-full ${s.ratio} overflow-hidden`}
            >
              <img
                src={s.src}
                alt={s.label}
                draggable={false}
                className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-ink-950/10 group-hover:bg-transparent transition-colors duration-500" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 group-hover:ring-magenta/60 transition-all duration-500" />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-4 font-display text-2xl tracking-[0.12em] text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                {s.label}
              </span>
            </button>
          </motion.div>
        ))}
        <div className="shrink-0 w-6 lg:w-10" aria-hidden="true" />
      </div>

      {/* Progress bar */}
      <div className="px-6 lg:px-16 max-w-[1400px] mx-auto mt-8">
        <div className="h-px w-full bg-white/10 relative overflow-hidden">
          <div
            data-testid="gallery-progress"
            className="absolute left-0 top-0 h-px bg-gradient-to-r from-violet to-magenta transition-[width] duration-150"
            style={{ width: `${8 + progress * 92}%` }}
          />
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-ink-950/95 backdrop-blur-md p-6"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 rounded-full glass p-3 text-white hover:text-magenta transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
              src={active.src}
              alt={active.label}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] object-contain glow-magenta"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
